import { EltType } from 'legacy/lut/classes/models/partner360/ELT/EltType'
import { IHpSob } from 'legacy/lut/classes/models/partner360/ELT/financialData/hpSob/IHpSob'
import { IELTFinancialData } from 'legacy/lut/classes/models/partner360/ELT/IELTFinancialData'
import {
  IAttendee,
  ICompetitor,
  IEltDocFormData,
  IELTFormData,
  IIssue,
  IMeeetingAgenda,
  IMeetingMainInfo,
  IOpportunity,
  IRevenue,
  ISupportTopic,
  TEltFormData,
} from 'legacy/lut/classes/models/partner360/ELT/IELTFormData'
import { IELTPopulatedData } from 'legacy/lut/classes/models/partner360/ELT/IELTPopulatedData'
import { isDocType } from 'legacy/lut/components/widgets/public/partner360/ELT/helpers/isDocType'

import { ActualsValueTypes } from 'partner360/models/actualValueTypes'

import { docCompanyAtGlance, pptxCompanyAtGlance } from '../configs'
import { IPartnerGroupAdditionalInfo } from '../models/IPartnerGroupAdditionalInfo'
import { getBoTInitialData } from '../ui/balanceOfTrade/helpers/getBoTInitialData'
import { getCompetitorsInitialData } from '../ui/financialSnapshot/competitors/getCompetitorsInitialData'
import { getHpSobInitialData } from '../ui/financialSnapshot/hpSob/helpers/getHpSobInitialData'
import { getMainInfoInitialData } from '../ui/meetingLogistics/mainInfo/helpers/getMainInfoInitialData'
import { getMeetingAgendaInitialData } from '../ui/meetingLogistics/meetingAgenda/helpers/getInitialData'
import { getLongPrioritiesInitialData } from '../ui/meetingReview/longTermPriorities/helpers/getLongPrioritiesInitialData'
import { getShortPrioritiesInitialData } from '../ui/meetingReview/shortTermPriorities/helpers/getShortPrioritiesInitialData'
import { getInitialOpportunitiesData } from '../ui/opportunities/helpers/getInitialOpportunitiesData'
import { getRevenueInitialData } from '../ui/revenue/helpers/getRevenueInitialData'
import { initialSupportData } from '../ui/supportReview/supportTopics/config'

import { getBoTByCompanyPerformanceRevenueInitialData } from './docInitialValues/getBoTByCompanyPerformanceRevenueInitialData'
import { getCompanyPerformanceInitialValues } from './docInitialValues/getCompanyPerformanceInitialValues'
import { getCustomerRevenueInitialData } from './docInitialValues/getCustomerRevenueInitialData'
import { getDocAdditionalNotesInitialValues } from './docInitialValues/getDocAdditionalNotesInitialValues'
import { getDocCompanyInfoInitialValues } from './docInitialValues/getDocCompanyInfoInitialValues'
import { getDocHpExecutiveKeyPointsInitialValues } from './docInitialValues/getDocHpExecutiveKeyPointsInitialValues'
import { getDocLastEngagementInitialValues } from './docInitialValues/getDocLastEngagementInitialValues'
import { getDocMeetinObjectivesInitialValues } from './docInitialValues/getDocMeetinObjectivesInitialValues'
import { getDocTopIssuesInitialValues } from './docInitialValues/getDocTopIssuesInitialValues'
import { getDocTopOpportunitiesInitialValues } from './docInitialValues/getDocTopOpportunitiesInitialValues'
import { getArraySectionInitialData } from './getArraySectionInitialData'
import { getEmptyMultiInputsRow } from './getEmptyMultiInputsRow'
import { getInitialCompanyAtGlance } from './getInitialCompanyAtGlance'
import { getLastELTEngagement } from './getLastELTEngagement'
import { getSectionInitialData } from './getSectionInitialData'
import { separateFinancialData } from './separateFinancialData'

interface IGetInitialDataPayload {
  formData: TEltFormData | null
  financialData: IELTFinancialData | null
  partnerGroupData: IPartnerGroupAdditionalInfo | null
  companyName: string
  populatedData: IELTPopulatedData
  itemEntryId?: number | null
  eltType: EltType
  actualValueTypes: ActualsValueTypes[]
  isRollup: number
}
const issueData: IIssue[] = [{ Issue: '', HpResponse: '', Status: 'New' }]

export const getInitialData = ({
  formData,
  financialData,
  partnerGroupData,
  itemEntryId,
  populatedData,
  eltType,
  companyName,
  actualValueTypes,
  isRollup,
}: IGetInitialDataPayload): TEltFormData => {
  const { revenueSnapshots, competitorsSnapshots } = separateFinancialData(financialData, eltType)
  const accountAttendees: IAttendee[] = [{ Email: '', Title: '', Name: '' }]
  const hpAttendees: IAttendee[] = [{ Email: '', Title: '', Name: '' }]

  const soldProducts = JSON.parse(populatedData.SoldProducts) || {}

  if (isDocType(eltType)) {
    const docFormData = formData as IEltDocFormData

    const companyAtGlance = getInitialCompanyAtGlance(docCompanyAtGlance, populatedData)
    const customerRevenueData = getCustomerRevenueInitialData(
      financialData?.CustomerRevenue || [],
      actualValueTypes
    )
    const companyPerformaceData = getCompanyPerformanceInitialValues(
      financialData?.CompanyPerformance || []
    )

    return {
      ...docFormData,
      LastEngagement: getDocLastEngagementInitialValues(),
      MeetingObjectives: getDocMeetinObjectivesInitialValues(),
      HpExecutiveKeyPoints: getDocHpExecutiveKeyPointsInitialValues(),
      AdditionalNotes: getDocAdditionalNotesInitialValues(),
      TopIssues: getDocTopIssuesInitialValues(),
      Company: {
        Name: `${companyName} - Customer Name – Date of Meeting`,
        CompanyInfo: getDocCompanyInfoInitialValues(),
        CompanyProfile: `${companyName} - Profile`,
      },
      CompetitiveLandscape: getArraySectionInitialData<ICompetitor>(
        docFormData?.CompetitiveLandscape,
        getCompetitorsInitialData(competitorsSnapshots, eltType)
      ),
      BalanceOfTrade: getBoTByCompanyPerformanceRevenueInitialData(
        companyPerformaceData,
        undefined,
        actualValueTypes
      ),
      TopOpportunities: getDocTopOpportunitiesInitialValues(),
      CustomerRevenue: customerRevenueData,
      CompanyAtGlanceInputs: companyAtGlance,
      CompanyPerformance: companyPerformaceData,
      FractionDigits: 2,
      IsRollup: isRollup,
    } as IEltDocFormData
  }

  const pptxFormData = formData as IELTFormData
  const lastELTEngagement = getLastELTEngagement(pptxFormData, populatedData)

  const companyAtGlance = getInitialCompanyAtGlance(pptxCompanyAtGlance, populatedData)

  return {
    ...formData,
    MeetingMainInfo: getSectionInitialData<IMeetingMainInfo>(
      pptxFormData?.MeetingMainInfo,
      getMainInfoInitialData(partnerGroupData, financialData)
    ),
    MeetingSummary: {
      Bio: `NAME/Title/Company
- Short bio of contact`,
    },
    HpAttendees: getArraySectionInitialData<IAttendee>(pptxFormData?.HpAttendees, hpAttendees),
    AccountAttendees: getArraySectionInitialData<IAttendee>(
      pptxFormData?.AccountAttendees,
      accountAttendees
    ),
    MeetingAgenda: getArraySectionInitialData<IMeeetingAgenda>(
      pptxFormData?.MeetingAgenda,
      getMeetingAgendaInitialData(pptxFormData?.MeetingMainInfo?.MeetingDateTime)
    ),
    FractionDigits: -1,
    LastEngagement: lastELTEngagement,
    LongTermPriorities: getArraySectionInitialData<string>(
      pptxFormData?.LongTermPriorities,
      getLongPrioritiesInitialData(financialData) || getEmptyMultiInputsRow()
    ),
    ShortTermPriorities: getArraySectionInitialData<string>(
      pptxFormData?.ShortTermPriorities,
      getShortPrioritiesInitialData(financialData) || getEmptyMultiInputsRow()
    ),
    MeetingPurpose: getArraySectionInitialData<string>(
      pptxFormData?.MeetingPurpose,
      getEmptyMultiInputsRow()
    ),
    SupportingPriorities: getArraySectionInitialData<string>(
      pptxFormData?.SupportingPriorities,
      getEmptyMultiInputsRow()
    ),
    NeedSupport: getArraySectionInitialData<string>(
      pptxFormData?.NeedSupport,
      getEmptyMultiInputsRow()
    ),
    SupportTopics: getArraySectionInitialData<ISupportTopic>(
      pptxFormData?.SupportTopics,
      initialSupportData
    ),
    CompetitiveLandscape: getArraySectionInitialData<ICompetitor>(
      pptxFormData?.CompetitiveLandscape,
      getCompetitorsInitialData(competitorsSnapshots, eltType)
    ),
    HPSob: getArraySectionInitialData<IHpSob>(
      pptxFormData?.HPSob,
      getHpSobInitialData(financialData?.HPSob)
    ),
    Revenue: getArraySectionInitialData<IRevenue>(
      pptxFormData?.Revenue,
      getRevenueInitialData(
        revenueSnapshots,
        populatedData?.Markets.split(';') || [],
        actualValueTypes
      )
    ),
    RevenueDesc: getSectionInitialData<string>(
      pptxFormData?.RevenueDesc,
      financialData?.SellThruDesc || ''
    ),
    FinancialPerformance: getArraySectionInitialData<string>(pptxFormData?.FinancialPerformance, [
      'Is account growing/declining? Explain why?',
      'Insert additional comments / narrative as applicable',
      'What do we need to reach the SOB ambition?',
    ]),
    GoodNewsDesc: getArraySectionInitialData<string>(
      pptxFormData?.GoodNewsDesc,
      getEmptyMultiInputsRow()
    ),
    Issues: getArraySectionInitialData<IIssue>(pptxFormData?.Issues, issueData),
    BalanceOfTrade: getBoTInitialData(pptxFormData?.BalanceOfTrade, actualValueTypes),
    BalanceOfTradeSummary: getArraySectionInitialData<string>(
      pptxFormData?.BalanceOfTradeSummary,
      getEmptyMultiInputsRow()
    ),
    KeyOpportunities: getArraySectionInitialData<IOpportunity>(
      pptxFormData?.KeyOpportunities,
      getInitialOpportunitiesData()
    ),
    SoldProductData: pptxFormData?.SoldProductData || soldProducts,
    // Old version of SoldProductData
    SoldPrintProducts: pptxFormData?.SoldPrintProducts,
    // Old version of SoldProductData
    SoldPSProducts: pptxFormData?.SoldPSProducts,
    // Old version of SoldProductData
    SoldServicesProducts: pptxFormData?.SoldServicesProducts,
    HiddenSections: itemEntryId
      ? pptxFormData?.HiddenSections
      : {
          BalanceOfTradeHidden: 1,
        },
    LastMeetingData: [],
    AdditionalSections: { RevenuePoly: financialData?.HasRevenuePolyData ?? 0 },
    CompanyAtGlanceInputs: companyAtGlance,
    PLC: financialData?.PLC,
    PLCParams: financialData?.PLCParams,
    IsRollup: isRollup,
  } as IELTFormData
}
